import { cn } from '@/lib/utils';
import type { LongueurControle } from '@rosetta/shared';

interface EchelleLongueurProps {
  longueur: LongueurControle;
  /** The status code of the indicator: the dot of the translation takes its colour. */
  statut: string;
}

/** Colour of the dot of the translation: the one of the status, as on its mention. */
const POINT: Readonly<Record<string, string>> = {
  bloquant: 'bg-rouge-deep',
  a_relire: 'bg-jaune-deep',
};

/** Where a value sits on the scale, in percent of its width. */
function echelle(valeurs: readonly number[]): (valeur: number) => number {
  const bas = Math.min(...valeurs);
  const haut = Math.max(...valeurs);
  const marge = Math.max((haut - bas) / 4, 1);
  const debut = Math.max(bas - marge, 0);
  const fin = haut + marge;
  return (valeur) => ((valeur - debut) / (fin - debut)) * 100;
}

/**
 * How a label hangs from its mark: centred, unless it would leave the scale (then it hangs
 * inwards) or meet the other label (then each one leans away from the other).
 */
function ancrage(position: number, autre: number | undefined): string {
  if (autre !== undefined && Math.abs(position - autre) < 24) {
    return position <= autre ? '-translate-x-full' : 'translate-x-0';
  }
  if (position < 12) return 'translate-x-0';
  if (position > 88) return '-translate-x-full';
  return '-translate-x-1/2';
}

const nombre = (valeur: number): string => valeur.toLocaleString('fr-FR');

/**
 * The scale of the "Longueur" card (indicator 3.1, A8): the range aimed at (dashed), the length
 * of the text of origin (a tick) and the length of the translation (a dot in the colour of the
 * status). Every number is Tahiti's. A picture for the eye; its name says it all in words.
 */
export function EchelleLongueur({ longueur, statut }: EchelleLongueurProps) {
  const { nombre: mots, min, max, unite, source } = longueur;
  const position = echelle([min, max, mots, ...(source === undefined ? [] : [source])]);
  const [gauche, droite, ici] = [position(min), position(max), position(mots)];
  const origine = source === undefined ? undefined : position(source);
  const nom = [
    ...(source === undefined ? [] : [`Texte d'origine : ${nombre(source)} ${unite}.`]),
    `Fourchette visée : ${nombre(min)} à ${nombre(max)} ${unite}.`,
    `Traduction : ${nombre(mots)} ${unite}.`,
  ].join(' ');

  return (
    <div role="img" aria-label={nom} className="relative mt-4 h-20 w-full max-w-[520px]">
      <span
        aria-hidden="true"
        className="absolute top-0 text-center text-gray-700 text-caption leading-[1.3]"
        style={{ left: `${gauche}%`, width: `${droite - gauche}%` }}
      >
        fourchette visée
      </span>
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-6 h-0 border-gray-200 border-t-2"
      />
      <span
        aria-hidden="true"
        className="absolute top-4 h-[18px] rounded-sm border border-gray-700 border-dashed bg-gray-50"
        style={{ left: `${gauche}%`, width: `${droite - gauche}%` }}
      />
      {origine === undefined ? null : (
        <span
          aria-hidden="true"
          className="absolute top-4 h-6 w-0.5 -translate-x-1/2 border-gray-700 border-l-2"
          style={{ left: `${origine}%` }}
        />
      )}
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-6 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full',
          POINT[statut] ?? 'bg-bleu-nuit',
        )}
        style={{ left: `${ici}%` }}
      />
      {origine === undefined || source === undefined ? null : (
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-12 whitespace-nowrap text-caption text-gray-700 leading-[1.3]',
            ancrage(origine, ici),
          )}
          style={{ left: `${origine}%` }}
        >
          texte d'origine&nbsp;: {nombre(source)}
        </span>
      )}
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-12 whitespace-nowrap font-semibold text-caption leading-[1.3]',
          ancrage(ici, origine),
        )}
        style={{ left: `${ici}%` }}
      >
        traduction&nbsp;: {nombre(mots)}
      </span>
    </div>
  );
}
